import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseClientKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Set to true while Supabase project credentials have not been configured
 * yet. The app runs entirely on dummy/local auth in this mode so the
 * foundation can be reviewed and deployed before wiring a backend.
 *
 * Flip automatically to false the moment both env vars are present.
 */
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseClientKey);

/** Never silently accept demo credentials in a production build. */
export const isDemoMode = !isSupabaseConfigured && import.meta.env.DEV;

/**
 * Typed Supabase client. Safe to import anywhere in the app even before a
 * project is configured — it is only ever called from behind
 * `isSupabaseConfigured` checks (see AuthContext) so an empty URL never
 * causes a runtime request.
 */
export const supabase = createClient<Database>(
  supabaseUrl || "https://placeholder.supabase.co",
  supabaseClientKey || "placeholder-anon-key",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
);

/**
 * ROW LEVEL SECURITY — NOTES FOR IMPLEMENTATION
 * -----------------------------------------------
 * This platform is multi-tenant at two levels: organization, then wedding.
 * When the real schema lands, every table below the tenant boundary should
 * carry an `organization_id` (and, where relevant, `wedding_id`) column
 * with RLS policies such as:
 *
 *   create policy "org members can read their org rows"
 *     on public.weddings for select
 *     using (
 *       organization_id in (
 *         select organization_id from public.organization_members
 *         where user_id = auth.uid()
 *       )
 *     );
 *
 * Role-gated writes (e.g. only "owner"/"admin" can delete a wedding)
 * should check the role column on organization_members in the same way,
 * rather than trusting any role claim sent from the client.
 */
